import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { AccountService } from '../../services/account.service';
import { FixedAssetCategoryService } from '../../services/fixedAssetCategory.service';
import { FixedAssetCategory, FixedAssetCategoryRequest } from '../../interfaces/fixedAsset/cwip.interface';
import { DepreciationMethod } from '../../interfaces/fixedAsset/fixedAsset.interface';
import { Account } from '../../interfaces/accounting.interface';
import { postingAccounts } from '../../utils/accountHierarchy';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { humanizeEnum } from '../../utils/format';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

type Form = {
  name: string; defaultMethod: DepreciationMethod; lifeMonths: string; ratePct: string; salvagePct: string;
  assetAccountId: string; accumulatedAccountId: string; expenseAccountId: string;
};
const EMPTY: Form = {
  name: '', defaultMethod: 'STRAIGHT_LINE', lifeMonths: '', ratePct: '', salvagePct: '0',
  assetAccountId: '', accumulatedAccountId: '', expenseAccountId: '',
};

const FixedAssetCategories: React.FC = () => {
  const canManage = usePermission(PERMISSIONS.FIXED_ASSET_MANAGE);
  // The GL accounts of a category are accounting setup: only the accounts team (SETTINGS_MANAGE) may set them.
  const canSetAccounts = usePermission(PERMISSIONS.FIXED_ASSET_SETTINGS_MANAGE);
  const toast = useToast();
  const [rows, setRows] = useState<FixedAssetCategory[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<FixedAssetCategory | 'new' | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [mapResult, setMapResult] = useState<string | null>(null);

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      setLoading(true);
      const [cats, accs] = await Promise.all([
        FixedAssetCategoryService.list(institutionId),
        AccountService.getLookup().catch(() => [] as Account[]),
      ]);
      setRows(cats);
      setAccounts(accs ?? []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load asset categories');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const leaves = useMemo(() => postingAccounts(accounts), [accounts]);
  const assetAccounts = leaves.filter(a => a.accountType === 'ASSET');
  const expenseAccounts = leaves.filter(a => a.accountType === 'EXPENSE');
  const accountLabel = (id?: string | null) => {
    const a = accounts.find(x => x.id === id);
    return a ? `${a.code} - ${a.name}` : '';
  };

  const openEdit = (c: FixedAssetCategory | 'new') => {
    setEditing(c);
    setForm(c === 'new' ? EMPTY : {
      name: c.name, defaultMethod: c.defaultMethod,
      lifeMonths: c.lifeMonths != null ? String(c.lifeMonths) : '',
      ratePct: c.ratePct != null ? String(c.ratePct) : '',
      salvagePct: String(c.salvagePct ?? 0),
      assetAccountId: c.assetAccountId ?? '', accumulatedAccountId: c.accumulatedAccountId ?? '',
      expenseAccountId: c.expenseAccountId ?? '',
    });
  };

  const reducing = form.defaultMethod === 'REDUCING_BALANCE';
  const invalid = !form.name.trim()
    || (reducing ? !(parseFloat(form.ratePct) > 0) : !(parseInt(form.lifeMonths, 10) > 0));

  const save = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    const req: FixedAssetCategoryRequest = {
      name: form.name.trim(), defaultMethod: form.defaultMethod,
      lifeMonths: form.lifeMonths ? parseInt(form.lifeMonths, 10) : undefined,
      ratePct: form.ratePct ? parseFloat(form.ratePct) : undefined,
      salvagePct: form.salvagePct ? parseFloat(form.salvagePct) : 0,
      // A caller without SETTINGS_MANAGE leaves the accounts out (the server keeps what is stored, or none for a new category).
      ...(canSetAccounts ? {
        assetAccountId: form.assetAccountId || undefined,
        accumulatedAccountId: form.accumulatedAccountId || undefined,
        expenseAccountId: form.expenseAccountId || undefined,
      } : {}),
    };
    try {
      setSaving(true);
      if (editing === 'new') await FixedAssetCategoryService.create(institutionId, req);
      else if (editing) await FixedAssetCategoryService.update(editing.id, req);
      toast.showSuccess('Category saved');
      setEditing(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c: FixedAssetCategory) => {
    try {
      if (c.active) await FixedAssetCategoryService.deactivate(c.id);
      else await FixedAssetCategoryService.activate(c.id);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to update category');
    }
  };

  const mapAll = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      const r = await FixedAssetCategoryService.mapCategories(institutionId);
      setMapResult(`Mapped ${r.mapped} asset(s); ${r.alreadyMapped} already mapped.`
        + (r.unmatchedNames.length ? ` No category named: ${r.unmatchedNames.join(', ')}.` : ''));
    } catch (e) {
      toast.showApiError(e, 'Failed to map categories');
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }} data-help-page="fixedAssets.cat.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Asset Categories</Typography>
        {canManage && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {canSetAccounts && <Button data-help="fixedAssets.cat.map" variant="outlined" onClick={mapAll}>Map categories</Button>}
            <Button data-help="fixedAssets.cat.new" variant="contained" startIcon={<AddIcon />} onClick={() => openEdit('new')}>New category</Button>
          </Box>
        )}
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        A category pre-fills method, life, rate, salvage % and the three accounts when you add an asset.
        {canSetAccounts
          ? '"Map categories" links existing assets to the category whose name matches their free-text category.'
          : 'The accounts of a category are set by the accounts team.'}
      </Alert>
      {mapResult && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMapResult(null)}>{mapResult}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small" data-help-section="fixedAssets.cat.table">
          <TableHead>
            <TableRow>
              <TableCell data-help="fixedAssets.cat.col.name">Name</TableCell><TableCell data-help="fixedAssets.cat.col.method">Method</TableCell><TableCell data-help="fixedAssets.cat.col.life" align="right">Life (months)</TableCell>
              <TableCell data-help="fixedAssets.cat.col.rate" align="right">Rate %</TableCell><TableCell data-help="fixedAssets.cat.col.salvage" align="right">Salvage %</TableCell>
              <TableCell data-help="fixedAssets.cat.col.account">Asset account</TableCell><TableCell data-help="fixedAssets.cat.col.status">Status</TableCell><TableCell data-help="fixedAssets.cat.col.actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">No categories yet</TableCell></TableRow>
            )}
            {rows.map(c => (
              <TableRow key={c.id} hover>
                <TableCell>{c.name}</TableCell>
                <TableCell>{humanizeEnum(c.defaultMethod)}</TableCell>
                <TableCell align="right">{c.lifeMonths ?? '—'}</TableCell>
                <TableCell align="right">{c.ratePct ?? '—'}</TableCell>
                <TableCell align="right">{c.salvagePct}</TableCell>
                <TableCell>
                  {accountLabel(c.assetAccountId)
                    || (!canSetAccounts && !c.accumulatedAccountId && !c.expenseAccountId
                      ? <Chip size="small" color="warning" variant="outlined" label="Accounts not set - ask accounts" />
                      : '—')}
                </TableCell>
                <TableCell><Chip size="small" label={c.active ? 'Active' : 'Inactive'} color={c.active ? 'success' : 'default'} /></TableCell>
                <TableCell align="right">
                  {canManage && (
                    <>
                      <Button data-help="fixedAssets.cat.action.edit" size="small" onClick={() => openEdit(c)}>Edit</Button>
                      <Button data-help="fixedAssets.cat.action.toggle" size="small" onClick={() => toggleActive(c)}>{c.active ? 'Deactivate' : 'Activate'}</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog data-help-page="fixedAssets.catForm.page" open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing === 'new' ? 'New category' : 'Edit category'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField data-help="fixedAssets.catForm.field.name" label="Name" required value={form.name} sx={{ mt: 1 }}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
          <TextField data-help="fixedAssets.catForm.field.method" select label="Default method" value={form.defaultMethod} fullWidth
            onChange={e => setForm(f => ({ ...f, defaultMethod: e.target.value as DepreciationMethod }))}>
            <MenuItem value="STRAIGHT_LINE">Straight line</MenuItem>
            <MenuItem value="REDUCING_BALANCE">Reducing balance</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField data-help="fixedAssets.catForm.field.life" label="Life (months)" type="number" required={!reducing} value={form.lifeMonths}
              sx={{ flex: '1 1 140px', maxWidth: 240 }}
              onChange={e => setForm(f => ({ ...f, lifeMonths: e.target.value }))} />
            <TextField data-help="fixedAssets.catForm.field.rate" label="Rate % per year" type="number" required={reducing} value={form.ratePct}
              sx={{ flex: '1 1 140px', maxWidth: 240 }}
              helperText={reducing ? 'Monthly charge = book value x rate / 12' : undefined}
              onChange={e => setForm(f => ({ ...f, ratePct: e.target.value }))} />
            <TextField data-help="fixedAssets.catForm.field.salvage" label="Salvage %" type="number" value={form.salvagePct}
              sx={{ flex: '1 1 140px', maxWidth: 240 }}
              onChange={e => setForm(f => ({ ...f, salvagePct: e.target.value }))} />
          </Box>
          {canSetAccounts ? (
            <>
              <AccountAutocomplete data-help="fixedAssets.catForm.field.assetAccount" accounts={assetAccounts} value={form.assetAccountId} label="Asset account"
                onChange={id => setForm(f => ({ ...f, assetAccountId: id }))} placeholder="Settings default" />
              <AccountAutocomplete data-help="fixedAssets.catForm.field.accumAccount" accounts={assetAccounts} value={form.accumulatedAccountId}
                label="Accumulated-depreciation account"
                onChange={id => setForm(f => ({ ...f, accumulatedAccountId: id }))} placeholder="Settings default" />
              <AccountAutocomplete data-help="fixedAssets.catForm.field.expenseAccount" accounts={expenseAccounts} value={form.expenseAccountId}
                label="Depreciation-expense account"
                onChange={id => setForm(f => ({ ...f, expenseAccountId: id }))} placeholder="Settings default" />
            </>
          ) : (
            <>
              <Alert severity={editing !== 'new' && (form.assetAccountId || form.accumulatedAccountId || form.expenseAccountId) ? 'info' : 'warning'}>
                {editing !== 'new' && (form.assetAccountId || form.accumulatedAccountId || form.expenseAccountId)
                  ? 'The accounts below are set by the accounts team and cannot be changed here.'
                  : 'Accounts not set. Save the category now and ask the accounts team to set its accounts; assets cannot be posted until they are.'}
              </Alert>
              <TextField data-help="fixedAssets.catForm.field.assetAccount" label="Asset account" value={accountLabel(form.assetAccountId) || 'Not set'}
                disabled fullWidth />
              <TextField data-help="fixedAssets.catForm.field.accumAccount" label="Accumulated-depreciation account"
                value={accountLabel(form.accumulatedAccountId) || 'Not set'} disabled fullWidth />
              <TextField data-help="fixedAssets.catForm.field.expenseAccount" label="Depreciation-expense account"
                value={accountLabel(form.expenseAccountId) || 'Not set'} disabled fullWidth />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-help="fixedAssets.catForm.cancel" onClick={() => setEditing(null)}>Cancel</Button>
          <Button data-help="fixedAssets.cat.action.save" variant="contained" onClick={save} disabled={invalid || saving}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FixedAssetCategories;
